/**
 * AudioWorkletLoader — robust multi-URL + retry wrapper around
 * `AudioContext.audioWorklet.addModule()`.
 *
 * Origin: 12 worklet-load failures / 30 days on /eight-bit-converter
 * (commit 21852ff51 telemetry pull) across 6 distinct sessions and 3
 * browsers (Chrome, Opera, Yandex), no CSP violations recorded. The
 * tool called `ctx.audioWorklet.addModule('/js/chiptune-worklet.js')`
 * directly — hardcoded URL, no `@light()` routing (non-RU users hit RU
 * origin), no SW caching (worklet 'destination' didn't match the
 * 'script' SW route until that was fixed 2026-05-02), no retry, no
 * fallback URL. One transient mobile-network blip → unrecoverable error.
 *
 * Pipeline:
 *
 *   For each URL in `urls` (in order):
 *     1. Try addModule(url)
 *     2. On rejection, retry once after a 250 ms backoff
 *     3. If both attempts fail, move on to the next URL
 *   If every URL exhausted, throw the last error.
 *
 * URLs: pass an array. Conventional order:
 *   [
 *     '{{ light("js/chiptune-worklet.js") }}',          // NL-routed for non-RU
 *     '{{ lightFallback("js/chiptune-worklet.js") }}',  // RU origin fallback
 *     '/js/chiptune-worklet.js'                          // last-resort relative
 *   ]
 *
 * Telemetry:
 *   - Per attempt: __ae('feature_use', {f:'worklet_load_attempt',
 *     slug, url, attempt, outcome:'ok'|'fail', err_msg, ms}).
 *   - Final success after at-least-one fail: __ae('feature_use',
 *     {f:'worklet_load_recovered', slug, total_attempts, ms}).
 *   - Total failure: __aeToolError({error_type:'worklet_load_failed',
 *     slug, urls_tried, last_err, br}). Sets window.__LAST_TOOL_ERROR.
 *
 * Usage:
 *   await window.AudioWorkletLoader.load(ctx, [
 *     '/cdn-light/js/foo-worklet.js',
 *     '/js/foo-worklet.js'
 *   ], { slug: 'eight-bit-converter' });
 *
 * Once the SW caches a successful load, subsequent calls hit the
 * cache and never see the network — but the loader is still cheap
 * (~150 lines) and worth keeping for cold-cache cases.
 */
(function() {
'use strict';

var BACKOFF_MS = 250;
var ATTEMPTS_PER_URL = 2;

function ae(payload) {
    try { if (window.__ae) window.__ae('feature_use', payload); } catch (_) {}
}

function aeError(payload) {
    try { if (window.__aeToolError) window.__aeToolError(payload); } catch (_) {}
}

function browserKey() {
    var ua = navigator.userAgent || '';
    if (/YaBrowser/i.test(ua)) return 'yandex';
    if (/Edg\//i.test(ua))     return 'edge';
    if (/OPR\//i.test(ua))     return 'opera';
    if (/Firefox/i.test(ua))   return 'firefox';
    if (/Chrome/i.test(ua))    return 'chrome';
    if (/Safari/i.test(ua))    return 'safari';
    return 'other';
}

function shortenUrl(url) {
    if (!url) return '';
    try {
        // Drop the scheme + host so the telemetry payload doesn't
        // bloat with `https://timbrica.com/...` repeated for every event.
        var u = new URL(url, location.href);
        return u.pathname + (u.search || '');
    } catch (_) {
        return String(url).substring(0, 200);
    }
}

function setLastError(slug, urls, err) {
    try {
        window.__LAST_TOOL_ERROR = {
            tool: slug || (window.__TOOL_SLUG || ''),
            stage: 'worklet_load',
            reason: 'worklet_load_failed',
            msg: ((err && err.message) || String(err) || '<empty>').substring(0, 200),
            urlsTried: (urls || []).length,
            ts: Date.now()
        };
    } catch (_) {}
}

async function tryOnce(ctx, url) {
    return await ctx.audioWorklet.addModule(url);
}

async function load(ctx, urls, opts) {
    opts = opts || {};
    var slug = opts.slug || (window.__TOOL_SLUG || '');

    if (!ctx || !ctx.audioWorklet || typeof ctx.audioWorklet.addModule !== 'function') {
        var noSupport = new Error('AudioWorklet not supported in this browser');
        noSupport.code = 'NO_AUDIOWORKLET';
        aeError({
            error_type: 'worklet_load_failed',
            slug: slug,
            err_msg: noSupport.message,
            br: browserKey(),
            reason: 'no_audioworklet'
        });
        setLastError(slug, [], noSupport);
        throw noSupport;
    }

    if (typeof urls === 'string') urls = [urls];
    if (!Array.isArray(urls) || urls.length === 0) {
        throw new Error('AudioWorkletLoader.load: urls must be non-empty');
    }

    var t0 = Date.now();
    var totalAttempts = 0;
    var lastErr = null;

    for (var i = 0; i < urls.length; i++) {
        var url = urls[i];
        if (!url) continue;
        for (var a = 1; a <= ATTEMPTS_PER_URL; a++) {
            var attemptT0 = Date.now();
            totalAttempts++;
            try {
                await tryOnce(ctx, url);
                var ms = Date.now() - attemptT0;
                ae({
                    f: 'worklet_load_attempt',
                    slug: slug,
                    url: shortenUrl(url),
                    attempt: a,
                    url_idx: i,
                    outcome: 'ok',
                    ms: ms,
                    br: browserKey()
                });
                if (totalAttempts > 1) {
                    ae({
                        f: 'worklet_load_recovered',
                        slug: slug,
                        total_attempts: totalAttempts,
                        url_idx: i,
                        ms: Date.now() - t0,
                        br: browserKey()
                    });
                }
                return;
            } catch (err) {
                lastErr = err;
                var msg = ((err && err.message) || String(err) || '<empty>').substring(0, 200);
                ae({
                    f: 'worklet_load_attempt',
                    slug: slug,
                    url: shortenUrl(url),
                    attempt: a,
                    url_idx: i,
                    outcome: 'fail',
                    err_msg: msg,
                    ms: Date.now() - attemptT0,
                    br: browserKey()
                });
                // Backoff before retrying on the SAME url. No backoff between URLs —
                // the next URL is a different host, network conditions might be
                // entirely different.
                if (a < ATTEMPTS_PER_URL) {
                    await new Promise(function(r) { setTimeout(r, BACKOFF_MS); });
                }
            }
        }
    }

    // Every URL exhausted.
    aeError({
        error_type: 'worklet_load_failed',
        slug: slug,
        err_msg: ((lastErr && lastErr.message) || String(lastErr) || '<empty>').substring(0, 200),
        urls_tried: urls.length,
        total_attempts: totalAttempts,
        ms: Date.now() - t0,
        br: browserKey()
    });
    setLastError(slug, urls, lastErr);
    throw lastErr || new Error('AudioWorkletLoader: all URLs failed');
}

window.AudioWorkletLoader = {
    load: load,
    _shortenUrl: shortenUrl,
    _browserKey: browserKey,
    version: '1.0.0'
};

})();
